import React, {useContext, useEffect, useRef, useState} from 'react';
import {ControlPosition, MapControl} from "@vis.gl/react-google-maps";
import {FaAngleLeft, FaAngleRight} from "react-icons/fa";
import {v4 as uuidv4} from "uuid";

import {PointLayerContext} from "./point-layer-context.jsx";

import InputForm from "../../../../components/input/input-form.jsx";
import PanelDefault from "./components/panel-default.jsx";
import PanelIcons from "./components/panel-icons.jsx";
import ImportKmzKml from "./components/import-kmz-kml.jsx";
import PanelPointDetails from "./components/panel-point-details.jsx";
import PointsList from "./components/points-list.jsx";
import {useMarkers} from "./hooks/use-markers.jsx";
import Button from "../../../../components/button/button.jsx";
import Alert from "../../../../components/alert/alert.jsx";
import PanelKmz from "./components/panel-kmz.jsx";
import pointLayerService from "../../../../services/web/point-layer-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import Swal from "sweetalert2";

const initialData = {
    name: '',
    number: '',
    street: '',
    city: '',
    neighborhood: '',
    observation: '',
}

const API_URL = import.meta.env.VITE_API_URL;

const PointLayerForm = () => {
    const navigate = navigateHook();
    const [nameLayer, setNameLayer] = useState("");
    const [itemsToRemove, setItemsToRemove] = useState([])

    const [showMenuPanel, setShowMenuPanel] = useState(true);
    const [showIconsPanel, setShowIconsPanel] = useState(false);
    const [showPointPanel, setShowPointPanel] = useState(false);
    const [showKmzPanel, setShowKmzPanel] = useState(false);
    const [showPointCacheListPanel, setShowPointCacheListPanel] = useState(true);

    const [kmzData, setKmzData] = useState([])
    const [pointSelected, setPointSelected] = useState();

    const {
        setIsLoading,
        map,
        iconSelected, setIconSelected,
        position,
        points,
        setPoints,
        markerLib,
        coreLib, id
    } = useContext(PointLayerContext);

    const pointsRef = useRef(points);
    pointsRef.current = points;

    const { createMarker, setMarkerEditing , updateIcon, removeMarker} = useMarkers({
        map,
        markerLib,
        coreLib,
        pointsRef,
        setPointSelected,
        setShowPointPanel,
        initialData,
        pointSelected
    });

    useEffect(() => {
        if (!showMenuPanel) {
            setShowKmzPanel(false)
            setShowPointPanel(false)
            setShowIconsPanel(false)
        }
    }, [showMenuPanel]);

    useEffect(() => {
        if (iconSelected) {
            updateIcon(iconSelected)
        }
    }, [iconSelected]);

    useEffect(() => {
        if (showPointPanel) {
            setShowMenuPanel(true)
            setMarkerEditing(pointSelected, true)
        } else {
            setMarkerEditing(pointSelected, false)
            setPointSelected(undefined)
        }
    }, [showPointPanel]);

    useEffect(() => {
        if (pointSelected) {
            map.setCenter(pointSelected.marker.getPosition());
        }
    }, [pointSelected]);

    const { data: pointLayerData, refetch, fetchStatus, isFetching } = useQuery({
        queryKey: ['point-layer'],
        queryFn: async () => await pointLayerService.getById(id),
        enabled: !!id
    });

    useEffect(() => {
        if (fetchStatus === "idle" && pointLayerData) {
            const {layer, points} = pointLayerData

            const bounds = new coreLib.LatLngBounds();
            const pointsPrepared = points.map(p => {
                const {name, number, street,city,neighborhood, observation, id, coordinates} = p;
                const data = {name, number, street,city,neighborhood, observation}

                const coordinatesParse = JSON.parse(coordinates)
                const lat = coordinatesParse[0]
                const lng = coordinatesParse[1]

                const point = createMarker({
                    id,
                    position: {lat, lng},
                    icon: layer.icon_name,
                    editing: false,
                    draggable: true
                })

                bounds.extend(new coreLib.LatLng(lat, lng));
                point.data = data
                return point
            })


            if (pointsPrepared.length) map.fitBounds(bounds);

            pointsRef.current = pointsPrepared
            setPoints(pointsPrepared)
            setNameLayer(layer.name)
            setIconSelected({marker: layer.icon_name, id: layer.icon_name, name: layer.icon_description})
        }
    }, [fetchStatus, pointLayerData]);

    const addPoint = () => {
        if (!iconSelected || pointSelected) return;

        const id = uuidv4();
        const point = createMarker({
            id,
            position,
            icon: iconSelected.marker,
        });

        setPoints(prev => [...prev, point]);
        setPointSelected(point);
        setShowPointPanel(true);
    }

    const save = () => {
        let messageError = ""

        if (!iconSelected) {
            messageError = "O campo \"Ícone\" é obrigatório.";
        }

        if (!nameLayer) {
            messageError = "O campo \"Nome\" é obrigatório.";
        }

        if (messageError) {
            projectUtils.swalErrorMessage("", messageError, "Ok");
            return;
        }

        setIsLoading(true)

        const pointsPrepared = points.filter(p => p.data.name).map(p => {
            const {data, marker} = p
            const coordinates = [];

            coordinates[0] = marker.position.lat();
            coordinates[1] = marker.position.lng();

            const address = data.street || null;
            const neighborhood = data.neighborhood || null;
            const city = data.city || null;

            return {
                id: marker.id,
                name: data.name,
                coordinates: coordinates,
                description: data.observation || null,
                number: data.number || null,
                city,
                address,
                neighborhood,
            }
        })

        const layer = {
            layerName: nameLayer,
            layerIconName: iconSelected.marker,
            layerIconText: iconSelected.name,
            layerId: id,
        }

        const formData = new FormData();
        formData.append("layer", JSON.stringify(layer));
        formData.append('points', JSON.stringify(pointsPrepared));
        formData.append("pointsRemoved", JSON.stringify(itemsToRemove));

        pointLayerService.save(formData)
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch()
                    return;
                }

                const urlDev = `/point-layer/form/${data.id}`;
                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    useEffect(() => {
        const cachePoints = points.filter(p => typeof p.id  === 'string')
        setShowPointCacheListPanel(cachePoints.length === 0)
    }, [points]);

    const remove = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir o ponto "${nameLayer}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                pointLayerService.remove(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso")
                        const urlDev = "/point-layer";
                        navigate(urlDev);
                    })
                    .catch(error => {
                        console.error(error);
                        let message = 'Não foi possível excluir.';
                        if (error.response.status !== 500) {
                            message = error.response.data.message;
                        }

                        projectUtils.swalErrorMessage('', message, 'Ok');
                    });
            }
        });
    }

    return (
        <>
            <MapControl position={ControlPosition.LEFT_TOP}>
                <PanelDefault
                    show={showMenuPanel}
                    setShow={setShowMenuPanel}
                    className={["z-1"]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <InputForm
                        id="name-point"
                        label="Nome *"
                        value={nameLayer}
                        onChange={e => setNameLayer(e.target.value)}
                    />

                    <div className="mt-2">
                        <label>Ícone *</label>

                        <div
                            className="mb-3 border border-[#bdbdbd] text-[#063A6C] rounded-md flex justify-between items-center px-2.5 h-8 cursor-pointer hover:bg-[#e7eaec]"
                            onClick={() => setShowIconsPanel(prev => !prev)}
                        >
                            {iconSelected ? (
                                <div className="flex items-center gap-2">
                                    <img alt={"icon"}
                                        className="max-h-5"
                                        src={`${import.meta.env.VITE_API_URL}/public/assets/img/markers/customLayers/normal/${iconSelected.marker}`}
                                    />
                                    <span className="font-bold text-xs truncate w-40">
                                    {iconSelected.name}
                                </span>
                                </div>
                            ) : (
                                <span className="text-xs font-bold">
                                    Selecionar ícone
                                </span>
                            )}

                            {showIconsPanel ? <FaAngleLeft /> : <FaAngleRight />}
                        </div>
                    </div>

                    <div className={"h-22.25"}>
                        {iconSelected && (
                            <>
                                <ImportKmzKml setShowKmzPanel={setShowKmzPanel} setKmzData={setKmzData} />

                                <div
                                    className="mt-3 border border-[#bdbdbd] text-[#063A6C] items-center rounded-md flex justify-between px-2.5 h-8 cursor-pointer hover:bg-[#e7eaec]"
                                    onClick={addPoint}
                                >
                                <span className="font-bold text-xs">
                                    Adicionar ponto
                                </span>
                                    {showPointPanel ? <FaAngleLeft /> : <FaAngleRight />}
                                </div>
                            </>
                        )}
                    </div>

                    <div className={"mt-3 h-[calc(100vh-327px)] overflow-y-auto overflow-x-hidden mb-3"}>
                        <PointsList
                            cacheList={false}
                            setShowPointPanel={setShowPointPanel}
                            setPointSelected={point => {
                                setMarkerEditing(point, true);
                                setPointSelected(point);
                            }}
                        />
                    </div>

                    <div className={"flex justify-end gap-2"}>
                        { id && <Button target={"danger"} onClick={remove} actionFeedbackType='delete'>Excluir</Button>}
                        <Button onClick={save} actionFeedbackType='save'>Salvar</Button>
                    </div>

                </PanelDefault>

                <PanelDefault
                    show={showIconsPanel}
                    setShow={setShowIconsPanel}
                    className={[`${showIconsPanel ? "left-62" : ""} z-0 p-5`]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <PanelIcons showPanel={showIconsPanel} setShowPanel={setShowIconsPanel}/>
                </PanelDefault>

                <PanelDefault
                    show={showPointPanel}
                    setShow={setShowPointPanel}
                    className={[`${showPointPanel ? "left-62" : ""} z-0 p-5 w-125`]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <PanelPointDetails
                        setItemsToRemove={setItemsToRemove}
                        map={map}
                        pointSelected={pointSelected}
                        setPointSelected={setPointSelected}
                        setShowPointPanel={setShowPointPanel}
                        showPointPanel={showPointPanel}
                        initialData={initialData}
                    />
                </PanelDefault>

                <PanelDefault
                    show={showKmzPanel}
                    setShow={setShowKmzPanel}
                    className={[`${showKmzPanel ? "left-62" : ""} z-0 p-5 w-125`]}
                    btnClassName={[showMenuPanel ? "-right-3" : "-right-6.5"]}
                >
                    <PanelKmz
                        kmzData={kmzData}
                        createMarker={createMarker}
                        iconSelected={iconSelected}
                        removeMarker={removeMarker}
                        showKmzPanel={showKmzPanel}
                        setShowKmzPanel={setShowKmzPanel}
                        coreLib={coreLib}
                        map={map}
                    />
                </PanelDefault>
            </MapControl>

            <MapControl position={ControlPosition.RIGHT_TOP}>
                <PanelDefault
                    show={showPointCacheListPanel}
                    setShow={setShowPointCacheListPanel}
                    className={["!-l-[250px] h-106.75! overflow-auto"]}
                    btnClassName={["hidden"]}
                >
                    <Alert target={"danger"}>
                        <span className={"text-[12px]"}><b>Atenção</b>: As alterações dos pontos abaixo ainda não foram aplicadas.</span>
                    </Alert>
                    <PointsList
                        cacheList={true}
                        setShowPointPanel={setShowPointPanel}
                        setPointSelected={point => {
                            setMarkerEditing(point, true);
                            setPointSelected(point);
                        }}
                    />
                </PanelDefault>
            </MapControl>

            <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <div className={"absolute -left-47.5 -bottom-31.5"}>
                    <img src={`${API_URL}/public/assets/img/sentry-logo2.png`} alt="image" style={{"width": "120px"}}/>
                </div>
            </MapControl>
        </>
    );
}

export default PointLayerForm;